import { Capacitor } from '@capacitor/core';
import { useState, useEffect, useCallback } from 'react';

import {
  Box,
  Step,
  Slide,
  Stack,
  Button,
  Dialog,
  Stepper,
  StepLabel,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';

import { checkBluetoothPermission } from 'src/hooks/use-device-permissions';

import { usePrinterStore } from 'src/store/PrinterStore';

import Iconify from 'src/components/iconify';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Phase = 'diagnostics' | 'reconnect' | 'result';

interface DiagnosticItem {
  label: string;
  ok: boolean;
  detail?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function PrinterTroubleshootDialog() {
  const { troubleshootOpen, closeTroubleshoot, pendingRetry, status, lastError, resetConnection } =
    usePrinterStore();

  const [phase, setPhase] = useState<Phase>('diagnostics');
  const [diagnostics, setDiagnostics] = useState<DiagnosticItem[]>([]);
  const [diagLoading, setDiagLoading] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [reconnectStep, setReconnectStep] = useState<string | null>(null);
  const [reconnectResult, setReconnectResult] = useState<'success' | 'fail' | null>(null);
  const [reconnectError, setReconnectError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [retryResult, setRetryResult] = useState<'success' | 'fail' | null>(null);

  // Reset state when dialog opens
  useEffect(() => {
    if (troubleshootOpen) {
      setPhase('diagnostics');
      setDiagnostics([]);
      setDiagLoading(false);
      setReconnecting(false);
      setReconnectStep(null);
      setReconnectResult(null);
      setReconnectError(null);
      setRetrying(false);
      setRetryResult(null);
    }
  }, [troubleshootOpen]);

  // Auto-run diagnostics when entering diagnostics phase
  useEffect(() => {
    if (!troubleshootOpen || phase !== 'diagnostics') return;

    const runDiagnostics = async () => {
      setDiagLoading(true);
      const items: DiagnosticItem[] = [];

      // Check Bluetooth permission
      if (Capacitor.isNativePlatform()) {
        try {
          const perm = await checkBluetoothPermission();
          items.push({
            label: 'Bluetooth Permission',
            ok: perm === 'granted',
            detail: perm === 'granted' ? 'Granted' : `Status: ${perm}`,
          });
        } catch {
          items.push({ label: 'Bluetooth Permission', ok: false, detail: 'Check failed' });
        }
      } else {
        items.push({ label: 'Bluetooth Permission', ok: true, detail: 'Desktop (N/A)' });
      }

      // Report printer status flags
      if (status) {
        items.push({ label: 'Printer Online', ok: !status.offline });
        items.push({ label: 'Paper Loaded', ok: !status.receiptPaperEmpty });
        items.push({ label: 'Cover Closed', ok: !status.coverOpen });
        items.push({ label: 'Cutter OK', ok: !status.cutterError });
        if (status.overTemp) {
          items.push({ label: 'Temperature', ok: false, detail: 'Overheating' });
        }
      } else {
        items.push({ label: 'Printer Status', ok: false, detail: 'No status — printer may be disconnected' });
      }

      // Report last error
      if (lastError) {
        const ago = Math.round((Date.now() - lastError.timestamp) / 1000);
        items.push({
          label: 'Last Error',
          ok: false,
          detail: `${lastError.message} (${ago}s ago, during ${lastError.context})`,
        });
      }

      setDiagnostics(items);
      setDiagLoading(false);
    };

    runDiagnostics();
  }, [troubleshootOpen, phase, status, lastError]);

  // Reconnect handler
  const handleReconnect = useCallback(async () => {
    setReconnecting(true);
    setReconnectStep(null);
    setReconnectResult(null);
    setReconnectError(null);
    try {
      await resetConnection((step) => setReconnectStep(step));
      const { isPrinterReady } = usePrinterStore.getState();
      if (isPrinterReady) {
        setReconnectResult('success');
        setPhase('result');
      } else {
        const { lastError: err } = usePrinterStore.getState();
        setReconnectResult('fail');
        setReconnectError(err?.message ?? 'Printer not ready after reconnect');
      }
    } catch (err) {
      setReconnectResult('fail');
      setReconnectError(err instanceof Error ? err.message : String(err));
    } finally {
      setReconnecting(false);
    }
  }, [resetConnection]);

  // Retry print handler
  const handleRetry = useCallback(async () => {
    if (!pendingRetry) return;
    setRetrying(true);
    setRetryResult(null);
    try {
      const ok = await pendingRetry();
      setRetryResult(ok ? 'success' : 'fail');
    } catch {
      setRetryResult('fail');
    } finally {
      setRetrying(false);
    }
  }, [pendingRetry]);

  const phaseMap: Record<Phase, number> = { diagnostics: 0, reconnect: 1, result: 2 };
  const phaseIndex = phaseMap[phase];

  return (
    <Dialog
      open={troubleshootOpen}
      onClose={closeTroubleshoot}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Slide}
      TransitionProps={{ direction: 'up' } as any}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Iconify icon="solar:printer-minimalistic-bold" width={24} />
          <span>Printer Troubleshoot</span>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        <Stepper activeStep={phaseIndex} alternativeLabel sx={{ mb: 3 }}>
          <Step completed={phaseIndex > 0}>
            <StepLabel>Diagnostics</StepLabel>
          </Step>
          <Step completed={phaseIndex > 1}>
            <StepLabel>Reconnect</StepLabel>
          </Step>
          <Step completed={reconnectResult === 'success'}>
            <StepLabel>Result</StepLabel>
          </Step>
        </Stepper>

        {/* ── Phase 1: Diagnostics ── */}
        {phase === 'diagnostics' && (
          <Box>
            {diagLoading ? (
              <Stack alignItems="center" spacing={1} sx={{ py: 3 }}>
                <CircularProgress size={28} />
                <Typography variant="body2" color="text.secondary">
                  Running diagnostics...
                </Typography>
              </Stack>
            ) : (
              <Stack spacing={1}>
                {diagnostics.map((item) => (
                  <Stack
                    key={item.label}
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={{
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                      bgcolor: item.ok ? 'success.lighter' : 'error.lighter',
                    }}
                  >
                    <Iconify
                      icon={item.ok ? 'solar:check-circle-bold' : 'solar:close-circle-bold'}
                      width={20}
                      sx={{ color: item.ok ? 'success.main' : 'error.main', flexShrink: 0 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2">{item.label}</Typography>
                      {item.detail && (
                        <Typography variant="caption" color="text.secondary">
                          {item.detail}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>
        )}

        {/* ── Phase 2: Reconnect ── */}
        {phase === 'reconnect' && (
          <Stack alignItems="center" spacing={2} sx={{ py: 2 }}>
            {reconnecting && (
              <>
                <CircularProgress size={40} />
                <Typography variant="body1">Resetting printer connection...</Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  {reconnectStep ?? 'Starting...'}
                </Typography>
              </>
            )}

            {!reconnecting && reconnectResult === 'fail' && (
              <>
                <Iconify icon="solar:close-circle-bold" width={48} sx={{ color: 'error.main' }} />
                <Typography variant="body1" color="error.main" fontWeight={600}>
                  Reconnect Failed
                </Typography>
                {reconnectError && (
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    {reconnectError}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" textAlign="center">
                  Try turning the printer off and on, then tap Retry
                </Typography>
                <Button variant="contained" onClick={handleReconnect}>
                  Retry
                </Button>
              </>
            )}

            {!reconnecting && reconnectResult !== 'fail' && (
              <>
                <Iconify
                  icon="solar:printer-minimalistic-bold"
                  width={48}
                  sx={{ color: 'text.secondary' }}
                />
                <Typography variant="body1">
                  Tap the button below to disconnect and re-establish the printer connection.
                </Typography>
                <Button variant="contained" size="large" onClick={handleReconnect} sx={{ minWidth: 200 }}>
                  Reconnect Printer
                </Button>
              </>
            )}
          </Stack>
        )}

        {/* ── Phase 3: Result ── */}
        {phase === 'result' && (
          <Stack alignItems="center" spacing={2} sx={{ py: 2 }}>
            <Iconify icon="solar:check-circle-bold" width={56} sx={{ color: 'success.main' }} />
            <Typography variant="h6" fontWeight={600}>
              Printer Connected
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The printer is ready to use.
            </Typography>

            {pendingRetry && (
              <Box sx={{ mt: 1 }}>
                {retryResult === 'success' && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Iconify icon="solar:check-circle-bold" width={20} sx={{ color: 'success.main' }} />
                    <Typography variant="body2" color="success.main" fontWeight={600}>
                      Print sent successfully
                    </Typography>
                  </Stack>
                )}

                {retryResult === 'fail' && (
                  <Stack alignItems="center" spacing={1}>
                    <Typography variant="body2" color="error.main">
                      Print failed — try again or close and retry manually
                    </Typography>
                    <Button variant="outlined" onClick={handleRetry} disabled={retrying}>
                      Retry Print
                    </Button>
                  </Stack>
                )}

                {retryResult === null && (
                  <Button
                    variant="contained"
                    onClick={handleRetry}
                    disabled={retrying}
                    startIcon={retrying ? <CircularProgress size={16} /> : undefined}
                    sx={{ minWidth: 180 }}
                  >
                    {retrying ? 'Printing...' : 'Retry Print'}
                  </Button>
                )}
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {phase === 'diagnostics' && (
          <>
            <Button onClick={closeTroubleshoot} color="inherit">
              Close
            </Button>
            <Button
              variant="contained"
              onClick={() => setPhase('reconnect')}
              disabled={diagLoading}
            >
              Reconnect
            </Button>
          </>
        )}
        {phase === 'reconnect' && (
          <Button onClick={() => setPhase('diagnostics')} color="inherit" disabled={reconnecting}>
            Back
          </Button>
        )}
        {phase === 'result' && (
          <Button variant="outlined" onClick={closeTroubleshoot}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
